import { useRef, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import type { Colors } from 'styled-components';
import { AnimatePresence, motion, useMotionValue } from 'framer-motion';

import { th } from '@yoweb/ui/styles/utils/theme';
import useVisualViewport from '@yoweb/ui/hooks/useVisualViewport';

// How far to scroll before the fade appears
const FADE_BUFFER = 12;

export type TabHorizontalContainerProps = {
  children: ReactNode;
  blendColor?: keyof Colors;
  /** html element to scroll the tab container to when it switches */
  scrollTo?: null | HTMLElement;
};

/**
 * TabHorizontalContainer.
 * Place TabHorizontal components into here to support proper spacing and fade out effects during scrolling.
 */
const TabHorizontalContainer = ({
  children,
  /** The background color behind the tabs to fade against. */
  blendColor = 'base000',
  scrollTo = null,
}: TabHorizontalContainerProps) => {
  const [hasLeftFade, setHasLeftFade] = useState(false);
  const [hasRightFade, setHasRightFade] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const constraintsRef = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);
  const visualViewPort = useVisualViewport();
  const [width, setWidth] = useState(-1);
  const [isMouseOver, setMouseOver] = useState(false);

  const [dragConstraints, setDragConstraints] = useState({
    left: 0,
    right: 0,
  });
  const updateDragConstraints = () => {
    if (!contentRef.current || !constraintsRef.current) {
      return;
    }
    setDragConstraints({
      left: -width,
      right: 0,
    });
  };
  // Setup initial faders
  useEffect(() => {
    updateDragConstraints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualViewPort.width]);

  useEffect(() => {
    if (scrollTo && constraintsRef.current) {
      const { left, right } = scrollTo.getBoundingClientRect();
      const { right: constraintRight } = constraintsRef.current.getBoundingClientRect();

      // if the scrollTo element is off screen from the left, scroll right until it's in view
      if (left < 0) {
        x.set(x.get() - left + FADE_BUFFER);
      }

      // if the scrollTo element is off screen from the right, scroll left until it's in view
      if (right > constraintRight) {
        x.set(x.get() + constraintRight - right - FADE_BUFFER);
      }
    }
  }, [x, scrollTo]);

  // Setup resize handlers.
  useEffect(() => {
    if (!contentRef.current || !constraintsRef.current) {
      return;
    }

    const parentWidth = constraintsRef.current.offsetWidth;
    const contentWidth = contentRef.current.scrollWidth;

    setWidth(Math.max(contentWidth - parentWidth, 0));
    x.set(0);
  }, [visualViewPort.width]);

  // Setup scroll handlers for mouse
  useEffect(() => {
    const onWheelMove = (event: globalThis.WheelEvent) => {
      // Don't steal y scroll
      if (!isMouseOver || event.deltaX === 0) {
        return;
      }

      x.stop();
      event.preventDefault();

      if (x.get() >= 0 && event.deltaX < 0) {
        x.set(0);
      } else if (x.get() <= -width && event.deltaX > 0) {
        x.set(-width);
      } else {
        x.set(x.get() - event.deltaX);
      }
    };

    window.addEventListener('wheel', onWheelMove, { passive: false });

    return () => {
      x.stop();
      window.removeEventListener('wheel', onWheelMove);
    };
  }, [x, width, isMouseOver]);

  const determineFaders = useCallback((xVal: number) => {
    if (!constraintsRef.current || !contentRef.current) return;
    // Determine if there is container overflow.
    const hasOverflow = constraintsRef.current.offsetWidth < contentRef.current.scrollWidth;

    // No overflow, never fade.
    if (!hasOverflow) {
      setHasLeftFade(false);
      setHasRightFade(false);
      return;
    }

    setHasLeftFade(xVal < -1 * FADE_BUFFER);

    const rightThreshold =
      contentRef.current.scrollWidth - constraintsRef.current.offsetWidth - FADE_BUFFER;
    setHasRightFade(xVal >= -1 * rightThreshold);
  }, []);

  // Add/remove left/right fade gradients during scroll
  useEffect(
    () =>
      x.on('change', (latest) => {
        determineFaders(latest);
      }),
    [x, determineFaders],
  );

  // Setup initial faders
  useEffect(() => {
    const initial = x.get();
    determineFaders(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Outer key={`motion-${visualViewPort.width}`} blendColor={blendColor} ref={constraintsRef}>
      <motion.div
        drag="x"
        dragConstraints={dragConstraints}
        onPointerDown={() => updateDragConstraints()}
        onDragStart={() => updateDragConstraints()}
        style={{
          x,
          cursor: 'grab',
        }}
        whileTap={{ cursor: 'grabbing' }}
        onMouseEnter={() => setMouseOver(true)}
        onMouseLeave={() => setMouseOver(false)}
      >
        <Inner ref={contentRef}>{children}</Inner>
      </motion.div>
      <AnimatePresence>
        {hasLeftFade && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <LeftFade blendColor={blendColor} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {hasRightFade && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <RightFade blendColor={blendColor} />
          </motion.div>
        )}
      </AnimatePresence>
    </Outer>
  );
};

const fadeBase = css`
  position: absolute;
  top: 0;
  height: 100%;
  width: 48px;
  z-index: ${th.getZIndex('layer')};
  pointer-events: none;
`;

const LeftFade = styled.div<{ blendColor: keyof Colors }>`
  ${fadeBase};

  left: 0;
  ${({ blendColor }) => css`
    background: linear-gradient(to right, ${th.getColor(blendColor)}, rgba(0 0 0 / 0%));
  `};
`;
const RightFade = styled.div<{ blendColor: keyof Colors }>`
  ${fadeBase};

  right: 0;
  ${({ blendColor }) => css`
    background: linear-gradient(to right, rgba(0 0 0 / 0%), ${th.getColor(blendColor)});
  `}
`;

const Outer = styled.nav<{ blendColor: keyof Colors }>`
  position: relative;
  overflow-x: hidden;
  padding-top: 3px;
  padding-bottom: 3px;

  ${({ blendColor }) => css`
    background: ${th.getColor(blendColor)};
  `}
`;

const Inner = styled(motion.div)`
  display: flex;
  gap: ${th.getSpace('small3')};
`;
export { TabHorizontalContainer };
